import {
  REPORT_BRAND_CSS,
  escapeHtml,
  loadReportCompany,
  reportBrandHtml,
} from './reportBranding';

const money = (v) =>
  Number(v || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const qty = (v) =>
  Number(v || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
};

/**
 * Abre impressão A4 / 80mm no padrão visual dos relatórios do Gestão.
 * Sem `noopener` — senão o browser devolve null e a página fica em branco.
 */
export async function abrirImpressaoPedidoComercial(imp, { modo = 'a4' } = {}) {
  if (!imp) return;

  const isA4 = modo !== '80mm';
  const popup = window.open(
    '',
    '_blank',
    isA4 ? 'width=900,height=1100' : 'width=340,height=720',
  );
  if (!popup) {
    throw new Error('Libere pop-ups para imprimir.');
  }

  const tituloDoc = imp.tipo === 'Pedido' ? 'Pedido de venda' : 'Orçamento';
  const company = await loadReportCompany();

  const clienteBits = [
    imp.clienteDocumento || imp.documento,
    imp.clienteTelefone,
    imp.clienteEmail,
    imp.clienteEndereco,
    imp.clienteCidade,
  ].filter(Boolean);

  if (isA4) {
    const rows = (imp.itens || [])
      .map(
        (i) => `<tr>
          <td>${escapeHtml(i.produtoCodigo || '')}</td>
          <td>${escapeHtml(i.produtoDescricao || '')}</td>
          <td class="num">${escapeHtml(qty(i.quantidade))}</td>
          <td class="num">${escapeHtml(money(i.valorUnitario))}</td>
          <td class="num">${Number(i.desconto) > 0 ? escapeHtml(money(i.desconto)) : '—'}</td>
          <td class="num">${escapeHtml(money(i.valorTotal))}</td>
        </tr>`,
      )
      .join('');

    popup.document.write(`<!doctype html><html><head><meta charset="utf-8"/>
      <title>${escapeHtml(imp.codigo || tituloDoc)}</title>
      <style>
        body{font:12px Arial,sans-serif;padding:18px;color:#172033}
        h1{margin:4px 0 0;font-size:18px}
        ${REPORT_BRAND_CSS}
        .meta{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;margin:14px 0;padding:12px;background:#f5f9fd;border:1px solid #e2e6ed;border-radius:8px}
        .meta strong{display:block;color:#5f6878;font-size:10px;text-transform:uppercase;letter-spacing:.04em}
        .meta span{font-size:13px;font-weight:700}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border-bottom:1px solid #e2e6ed;padding:7px 6px;text-align:left;vertical-align:top}
        th{background:#eaf6fe;color:#0b5f99;font-size:11px}
        td.num,th.num{text-align:right;white-space:nowrap}
        .totais{margin-top:14px;text-align:right}
        .totais .linha{font-size:13px;margin:2px 0}
        .totais .total{font-size:18px;font-weight:900;color:#0495f0;margin-top:6px}
        .obs{margin-top:16px;padding:10px;border:1px dashed #cfd6e0;border-radius:8px;color:#445}
        .barcode{text-align:center;margin-top:18px}
        .footer{margin-top:20px;font-size:10px;color:#8a93a3;text-align:center}
        @media print{body{padding:0}.noprint{display:none}}
      </style></head><body>
      <div class="noprint" style="margin-bottom:10px">
        <button onclick="window.print()" style="padding:8px 14px;font-weight:700;cursor:pointer">Imprimir</button>
      </div>
      ${reportBrandHtml(company, {
        title: tituloDoc,
        documentLabel: imp.codigo || '',
        fallbackName: imp.estabelecimentoNome || 'Estabelecimento',
      })}
      <div class="meta">
        <div><strong>Cliente</strong><span>${escapeHtml(imp.clienteNome || 'Consumidor')}</span>
          ${clienteBits.length ? `<div style="font-weight:500;font-size:11px;color:#5f6878;margin-top:2px">${escapeHtml(clienteBits.join(' · '))}</div>` : ''}
        </div>
        <div><strong>Status</strong><span>${escapeHtml(imp.status || '')}</span></div>
        <div><strong>Emissão</strong><span>${escapeHtml(fmtDate(imp.dataCriacao))}</span></div>
        <div><strong>Validade</strong><span>${escapeHtml(imp.validade ? fmtDate(imp.validade).slice(0, 10) : '—')}</span></div>
        <div><strong>Pagamento</strong><span>${escapeHtml(imp.formaPagamentoNome || '—')}</span></div>
        <div><strong>Código</strong><span>${escapeHtml(imp.codigo || '')}</span></div>
      </div>
      <table>
        <thead><tr>
          <th>Código</th><th>Descrição</th>
          <th class="num">Qtd</th><th class="num">Unit.</th>
          <th class="num">Desc.</th><th class="num">Total</th>
        </tr></thead>
        <tbody>${rows || '<tr><td colspan="6">Sem itens</td></tr>'}</tbody>
      </table>
      <div class="totais">
        ${Number(imp.desconto) > 0 ? `<div class="linha">Desconto: ${escapeHtml(money(imp.desconto))}</div>` : ''}
        ${Number(imp.acrescimo) > 0 ? `<div class="linha">Acréscimo: ${escapeHtml(money(imp.acrescimo))}</div>` : ''}
        <div class="total">TOTAL ${escapeHtml(money(imp.valorTotal))}</div>
      </div>
      ${imp.observacao ? `<div class="obs"><strong>Observação</strong><br/>${escapeHtml(imp.observacao)}</div>` : ''}
      <div class="barcode">
        <svg id="bc"></svg>
        <div style="font-weight:800;letter-spacing:.08em;margin-top:4px">${escapeHtml(imp.codigo || '')}</div>
      </div>
      <div class="footer">Documento gerado pelo ZenGestão · ${escapeHtml(tituloDoc)}</div>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
      <script>
        try {
          if (${JSON.stringify(Boolean(imp.codigo))}) {
            JsBarcode('#bc', ${JSON.stringify(String(imp.codigo || ''))}, {
              format: 'CODE128', displayValue: false, height: 52, margin: 0, width: 1.8
            });
          }
        } catch (e) {}
        setTimeout(function(){ window.print(); }, 350);
      <\/script>
    </body></html>`);
  } else {
    // Cupom térmico 80mm: NÃO usar margin:auto (no Chrome vira faixa no meio do A4).
    const itemRows = (imp.itens || [])
      .map((i) => {
        const desc = Number(i.desconto) > 0 ? ` (-${money(i.desconto)})` : '';
        return `<div class="item">
          <div class="item-desc">${escapeHtml(i.produtoCodigo || '')} ${escapeHtml(i.produtoDescricao || '')}</div>
          <div class="item-line">
            <span>${escapeHtml(qty(i.quantidade))} x ${escapeHtml(money(i.valorUnitario))}${escapeHtml(desc)}</span>
            <span>${escapeHtml(money(i.valorTotal))}</span>
          </div>
        </div>`;
      })
      .join('');

    popup.document.write(`<!doctype html><html><head><meta charset="utf-8"/>
      <meta name="viewport" content="width=80mm, initial-scale=1"/>
      <title>${escapeHtml(imp.codigo || tituloDoc)}</title>
      <style>
        @page { size: 80mm auto; margin: 0; }
        * { box-sizing: border-box; }
        html, body {
          width: 80mm !important;
          max-width: 80mm !important;
          min-width: 80mm !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff;
          color: #000;
          font: 12px/1.35 Arial, Helvetica, sans-serif;
        }
        .ticket {
          width: 72mm;
          max-width: 72mm;
          margin: 0;
          padding: 3mm 4mm 6mm;
        }
        .brand { text-align: center; font-weight: 800; font-size: 13px; margin-bottom: 4px; }
        .doc { text-align: center; font-weight: 800; font-size: 12px; text-transform: uppercase; }
        .code { text-align: center; font-weight: 900; letter-spacing: .06em; margin: 2px 0 6px; }
        .sep { border-top: 1px dashed #000; margin: 6px 0; }
        .meta { font-size: 11px; }
        .meta div { margin: 1px 0; }
        .item { margin: 5px 0; }
        .item-desc { font-weight: 700; font-size: 11px; word-break: break-word; }
        .item-line { display: flex; justify-content: space-between; gap: 6px; font-size: 11px; }
        .totais { margin-top: 4px; font-size: 12px; }
        .totais .row { display: flex; justify-content: space-between; }
        .totais .total { font-size: 15px; font-weight: 900; margin-top: 4px; }
        .barcode { text-align: center; margin-top: 8px; }
        .barcode svg { max-width: 100%; height: auto; }
        .hint { font-size: 10px; color: #444; margin-top: 8px; text-align: center; }
        .noprint { padding: 8px; }
        @media print {
          .noprint { display: none !important; }
          html, body, .ticket { width: 80mm !important; max-width: 80mm !important; }
        }
      </style></head><body>
      <div class="noprint">
        <button onclick="window.print()" style="padding:8px 12px;font-weight:700;cursor:pointer">Imprimir</button>
        <div class="hint">Na impressora T20, em &quot;Mais definições&quot;, escolha papel <b>80 mm</b> / rolo e escala <b>100%</b>.</div>
      </div>
      <div class="ticket">
        <div class="brand">${escapeHtml(company.nomeFantasia || company.razaoSocial || imp.estabelecimentoNome || '')}</div>
        <div class="doc">${escapeHtml(tituloDoc)}</div>
        <div class="code">${escapeHtml(imp.codigo || '')}</div>
        <div class="meta">
          <div>Data: ${escapeHtml(fmtDate(imp.dataCriacao))}</div>
          <div>Cliente: ${escapeHtml(imp.clienteNome || 'Consumidor')}</div>
          ${(imp.clienteDocumento || imp.documento) ? `<div>Doc: ${escapeHtml(imp.clienteDocumento || imp.documento)}</div>` : ''}
          ${imp.formaPagamentoNome ? `<div>Pagto: ${escapeHtml(imp.formaPagamentoNome)}</div>` : ''}
        </div>
        <div class="sep"></div>
        ${itemRows || '<div class="item">Sem itens</div>'}
        <div class="sep"></div>
        <div class="totais">
          ${Number(imp.desconto) > 0 ? `<div class="row"><span>Desconto</span><span>${escapeHtml(money(imp.desconto))}</span></div>` : ''}
          <div class="row total"><span>TOTAL</span><span>${escapeHtml(money(imp.valorTotal))}</span></div>
        </div>
        ${imp.observacao ? `<div class="sep"></div><div class="meta">${escapeHtml(imp.observacao)}</div>` : ''}
        <div class="barcode"><svg id="bc"></svg></div>
        <div class="code">* ${escapeHtml(imp.codigo || '')} *</div>
      </div>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
      <script>
        try {
          if (${JSON.stringify(Boolean(imp.codigo))}) {
            JsBarcode('#bc', ${JSON.stringify(String(imp.codigo || ''))}, {
              format: 'CODE128', displayValue: false, height: 36, margin: 0, width: 1.5
            });
          }
        } catch (e) {}
        setTimeout(function(){ window.print(); }, 350);
      <\/script>
    </body></html>`);
  }

  popup.document.close();
  try {
    if (!isA4) {
      popup.resizeTo(340, 720);
    }
  } catch (e) { /* ignore */ }
}
